import { useApolloClient, useMutation } from "@apollo/client";
import {
  Box,
  Button,
  Center,
  Highlight,
  HStack,
  IconButton,
  Spinner,
  Stack,
  SystemStyleObject,
  Text,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import {
  CreatableSelect,
  SelectInstance,
  SingleValue,
} from "chakra-react-select";
import * as EmailValidator from "email-validator";
import {
  CreateUserListMutation,
  CreateUserListMutationVariables,
  UserFragment,
  UserInput,
  UserListQuery,
  UserRoleType,
} from "gql/graphql";
import {
  CloseIcon,
  InfoIcon,
  EnvelopeIcon,
  UserEmailCircleIcon,
} from "modules/icons";
import CREATE_USER_LIST_MUTATION from "modules/messaging/_networking/create-user-list";
import { useUsers } from "modules/providers/users-provider";
import USER_LIST_QUERY from "modules/providers/users-provider/networking/user-list";
import React, {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import colors from "app/colors";
import { CALLOUT_KEY, IEmailAddress } from "modules/utils/Constants";
import { getCleanEmail, getUserFullName } from "modules/utils/Functions";
import mergeOrInsertObject from "modules/utils/merge-or-insert-object";
import { IIsNew } from "gql/isNew";

interface IProps {
  callback?: (userIds: string[]) => void;
  onClose?: () => void;
}

// This interface is needed so it's more straight forward to render a "new" user (email user) in the CreatableSelect
interface IEmailUserFragment extends UserFragment, IIsNew {
  label: string;
  value: string;
}

const AddEmails = ({ callback, onClose }: IProps) => {
  const { cache } = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = React.useState("");
  //used to trigger auto-adding an email address based on the user's input
  const [isAddEmailAddress, setIsAddEmailAddress] = React.useState(false);
  const [emailAddresses, setEmailAddresses] = React.useState<IEmailAddress[]>(
    [],
  );
  const [error, setError] = React.useState<ReactNode | string>();
  const { exposedEmailUsers, users, usersById } = useUsers();
  const [pendingUserIds, setPendingUserIds] = useState<string[]>([]);

  const selectRef = useRef<SelectInstance<IEmailUserFragment>>(null);

  const emailUserList = useMemo(
    () =>
      exposedEmailUsers
        .filter(
          (obj) =>
            !emailAddresses.map((obj) => obj.email).includes(obj.email ?? ""),
        )
        .map(
          (emailUser): IEmailUserFragment => ({
            ...emailUser,
            __isNew__: false,
            label: emailUser.email ?? "",
            value: emailUser.id,
          }),
        ),
    [emailAddresses, exposedEmailUsers],
  );

  const [createUserListAPI] = useMutation<
    CreateUserListMutation,
    CreateUserListMutationVariables
  >(CREATE_USER_LIST_MUTATION);

  const onAddClicked = useCallback(() => {
    setError(undefined);
    const emailAddressList = [...emailAddresses];
    const cleanEmail = getCleanEmail(email);
    if (cleanEmail.length > 0) {
      const emailAddress: IEmailAddress = {
        email: cleanEmail,
        isValid: EmailValidator.validate(cleanEmail),
      };
      emailAddressList.push(emailAddress);
      setEmailAddresses(emailAddressList);
      setEmail("");
    }
    if (emailAddressList.filter((obj) => obj.isValid === false).length === 0) {
      setIsSubmitting(true);
      const userInputList: UserInput[] = [];
      emailAddressList.forEach((emailAddress) => {
        userInputList.push({
          email: emailAddress.email,
        });
      });
      createUserListAPI({
        variables: {
          input: {
            userInputList,
          },
        },
        onCompleted: (data) => {
          const userList = data?.createUserList;
          if (!userList) {
            return;
          }
          cache.updateQuery<UserListQuery>(
            {
              query: USER_LIST_QUERY,
            },
            (data) => {
              if (!data) {
                return data;
              }
              const newUserList = data.userList;
              userList.forEach((newUser) =>
                mergeOrInsertObject(
                  newUserList,
                  newUser,
                  (cachedUser) => cachedUser.id === newUser.id,
                  false,
                ),
              );
              return { ...data, userList: newUserList };
            },
          );
          setPendingUserIds(userList.map((obj) => obj.id));
          setIsSubmitting(false);
          setEmailAddresses([]);
        },
        onError: (error: Error) => {
          setIsSubmitting(false);
          setError(error.message);
        },
      });
    }
  }, [emailAddresses, email, createUserListAPI, cache]);

  useEffect(() => {
    if (pendingUserIds.length > 0) {
      const allFound = pendingUserIds.every((id) => usersById[id]);
      if (allFound) {
        callback?.(pendingUserIds);
        setPendingUserIds([]);
        onClose?.();
      }
    }
  }, [pendingUserIds, callback, onClose, usersById]);

  const addEmailAddress = useCallback((emailString: string) => {
    const cleanEmailString = getCleanEmail(emailString);
    setEmailAddresses((state) => [
      ...state.filter((email) => email.email !== cleanEmailString),
      {
        email: cleanEmailString,
        isValid: EmailValidator.validate(cleanEmailString),
      },
    ]);
    setEmail("");
  }, []);

  const onItemCreated = useCallback(
    (newValue: string) => {
      const userList = users.filter(
        (user) =>
          user.roleType !== UserRoleType.Email &&
          user.email &&
          user.email.trim().toLowerCase() === newValue,
      );
      if (userList.length > 0) {
        const user = userList[0];
        setError(
          <>
            The email address{" "}
            <Text as="span" fontWeight={700}>
              {newValue}
            </Text>{" "}
            is associated with user{" "}
            <Text as="span" fontWeight={700}>
              {CALLOUT_KEY}
              {user.callout}
            </Text>
            . Insert that callout in the message to add them to this
            conversation.
          </>,
        );
        return;
      }
      addEmailAddress(newValue);
    },
    [addEmailAddress, users],
  );

  const onInputChanged = useCallback(
    (newValue: string) => {
      const cleanEmail = newValue.replace(/\s/g, "");
      setEmail(cleanEmail);
      if (isAddEmailAddress) {
        onItemCreated(cleanEmail);
        setIsAddEmailAddress(false);
      }
    },
    [isAddEmailAddress, onItemCreated],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === " ") {
        setIsAddEmailAddress(true);
      }
    },
    [],
  );

  const onItemSelected = useCallback(
    (selectedUser: SingleValue<UserFragment>) => {
      if (selectedUser && selectedUser.email) {
        addEmailAddress(selectedUser.email);
      }
    },
    [addEmailAddress],
  );

  const onEmailClicked = useCallback(
    (emailAddress: IEmailAddress) => () => {
      setEmailAddresses((state) => state.filter((obj) => obj !== emailAddress));
    },
    [],
  );

  const hasInvalidEmailAddresses = useCallback(
    () => emailAddresses.filter((obj) => obj.isValid === false).length !== 0,
    [emailAddresses],
  );

  const isValid = useMemo(
    () => !hasInvalidEmailAddresses(),
    [hasInvalidEmailAddresses],
  );

  const onCancelClicked = useCallback(() => {
    if (onClose) {
      onClose();
    }
    setError(undefined);
    setEmailAddresses([]);
    setIsSubmitting(false);
  }, [onClose]);

  const clearError = useCallback(() => {
    setError(undefined);
  }, []);

  useEffect(() => {
    if (selectRef.current && selectRef.current.inputRef) {
      selectRef.current.inputRef.focus();
    }
  }, [selectRef.current?.inputRef]);

  const renderEmailUser = useCallback(
    (user: IEmailUserFragment) =>
      user.__isNew__ ? (
        <HStack>
          <UserEmailCircleIcon boxSize={5} fill={colors.white} />
          <EnvelopeIcon boxSize={4} fill={colors.blueGreen} />
          <Text color={colors.blueGreen} noOfLines={1}>
            {user.value}
          </Text>
        </HStack>
      ) : (
        <HStack>
          <UserEmailCircleIcon boxSize={5} fill={colors.white} />
          <EnvelopeIcon boxSize={4} fill={colors.blueGreen} />
          <Text color={colors.blueGreen} noOfLines={1}>
            <Highlight
              query={email}
              styles={{
                fontWeight: 700,
                color: colors.blueGreen,
                margin: "0 !important",
              }}
            >
              {user?.email || ""}
            </Highlight>
          </Text>
        </HStack>
      ),
    [email],
  );

  const getEmailUserLabel = useCallback(
    (user: UserFragment) => `${user.email} ${getUserFullName(user)}`,
    [],
  );

  return (
    <Box w="full" borderRadius={50} p={4} zIndex="popover">
      <Stack w="full">
        <Center w="full">
          <Text fontWeight={700} fontSize={18}>
            Add Email
          </Text>
        </Center>
        <Center w="full">
          <Text color={colors.gray50} fontSize={16} textAlign="center">
            Include people outside your organization in this conversation by
            adding their email address. Note they will receive all messages in
            conversation.
          </Text>
        </Center>
        {!isValid && (
          <HStack
            w="full"
            color={colors.red}
            backgroundColor={colors.redLight}
            borderRadius={8}
            p={1}
          >
            <InfoIcon boxSize={6} ml={2} fill={colors.red} />
            <Text fontWeight={500} fontSize={14}>
              One or more email addresses are invalid.
            </Text>
          </HStack>
        )}
        {error !== undefined && (
          <HStack
            w="full"
            color={colors.red}
            backgroundColor={colors.redLight}
            borderRadius={8}
            p={1}
          >
            <InfoIcon boxSize={6} ml={2} fill={colors.red} />
            <Text fontWeight={500} fontSize={14}>
              {error}
            </Text>
            <IconButton
              h={6}
              top={-5}
              minW={6}
              aria-label="Cancel"
              variant="ghost"
              icon={<CloseIcon fill={colors.redDark} />}
              onClick={clearError}
            />
          </HStack>
        )}
        <Center>
          <Box w="full">
            <CreatableSelect
              options={emailUserList}
              autoFocus
              onInputChange={onInputChanged}
              onKeyDown={onKeyDown}
              onChange={onItemSelected}
              onCreateOption={onItemCreated}
              components={{
                DropdownIndicator: () => null,
                IndicatorSeparator: () => null,
              }}
              placeholder="Enter email address"
              menuPlacement="top"
              chakraStyles={{
                placeholder: (
                  provided: SystemStyleObject,
                ): SystemStyleObject => ({
                  ...provided,
                  fontStyle: "italic",
                }),
              }}
              inputValue={email}
              value={null}
              formatOptionLabel={renderEmailUser}
              getOptionLabel={getEmailUserLabel}
            />
          </Box>
        </Center>
        <Wrap>
          {emailAddresses.map((emailAddress, index) => (
            <WrapItem key={index}>
              <Button
                h="fit-content"
                iconSpacing={1.5}
                px={2}
                py={1}
                border="unset"
                rounded="full"
                variant="ghost"
                onClick={onEmailClicked(emailAddress)}
                color={emailAddress.isValid ? colors.blueGreen : colors.redDark}
                leftIcon={
                  <EnvelopeIcon
                    fill={
                      emailAddress.isValid ? colors.blueGreen : colors.redDark
                    }
                    boxSize={4}
                  />
                }
                rightIcon={
                  <CloseIcon
                    fill={
                      emailAddress.isValid ? colors.blueGreen : colors.redDark
                    }
                  />
                }
                backgroundColor={
                  emailAddress.isValid ? colors.blueGreen10 : colors.redLight
                }
              >
                {emailAddress.email}
              </Button>
            </WrapItem>
          ))}
        </Wrap>
        <Center pt={5}>
          <HStack spacing={8}>
            <Button variant="cancel" w={139} onClick={onCancelClicked}>
              Cancel
            </Button>
            <Button
              w={139}
              type="submit"
              isDisabled={!isValid || emailAddresses.length === 0}
              onClick={onAddClicked}
              _disabled={{
                background: `${colors.gray80} !important`,
                opacity: "1 !important",
              }}
            >
              {isSubmitting ? <Spinner /> : "Add"}
            </Button>
          </HStack>
        </Center>
      </Stack>
    </Box>
  );
};

export default memo(AddEmails);
